import { supabase } from '../../../shared/lib/supabaseClient';
import { logActivity } from '../../../shared/services/activityLogService';
import { escapeLike } from '../../../shared/utils/escapeLike';

const SUBJECT_SELECT = '*, teachers(id, firstname, lastname), subject_sections(section)';

export async function getSubjects({ search = '', category = '', gradeLevel = '', page = 1, pageSize = 10 } = {}) {
  let query = supabase
    .from('subjects')
    .select(SUBJECT_SELECT, { count: 'exact' })
    .order('name', { ascending: true });

  if (search) query = query.ilike('name', `%${search}%`);
  if (category) query = query.eq('category', category);
  if (gradeLevel) query = query.eq('grade_level', gradeLevel);

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  const { data, error, count } = await query.range(from, to);
  if (error) throw error;
  return { data, count: count ?? 0 };
}

export async function getAllSubjects({ gradeLevel = '', schoolYear = '' } = {}) {
  let query = supabase.from('subjects').select(SUBJECT_SELECT).order('name', { ascending: true });
  if (gradeLevel) query = query.eq('grade_level', gradeLevel);
  if (schoolYear) query = query.eq('school_year', schoolYear);

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getOwnSubjects() {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user?.email) return [];

  const { data, error } = await supabase
    .from('subjects')
    .select('id, name, category, grade_level, teachers!inner(email), subject_sections(section)')
    .ilike('teachers.email', escapeLike(user.email))
    .order('name', { ascending: true });
  if (error) throw error;
  return data;
}

// The grade level + section pairs the signed-in teacher teaches, taken from their assigned
// subjects: [{ grade_level, section }]. Empty when nothing is assigned yet.
export function toSectionGroups(subjects) {
  const groups = new Map();
  subjects.forEach((subject) => {
    if (!subject.grade_level) return;
    subject.subject_sections.forEach(({ section }) => {
      groups.set(`${subject.grade_level}|${section}`, { grade_level: subject.grade_level, section });
    });
  });
  return [...groups.values()];
}

export async function getOwnSectionGroups() {
  return toSectionGroups(await getOwnSubjects());
}

// Returns null when the subject doesn't exist or isn't assigned to the signed-in teacher.
export async function getOwnSubject(id) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user?.email) return null;

  const { data, error } = await supabase
    .from('subjects')
    .select(
      'id, name, category, grade_level, school_year, ww_weight, pt_weight, exam_weight, att_weight, teachers!inner(email, firstname, middlename, lastname), subject_sections(section)'
    )
    .eq('id', id)
    .ilike('teachers.email', escapeLike(user.email))
    .maybeSingle();
  if (error) throw error;
  return data;
}

// Replaces the sections that take a subject with exactly `sections`.
async function replaceSubjectSections(subjectId, sections) {
  const { error: deleteError } = await supabase.from('subject_sections').delete().eq('subject_id', subjectId);
  if (deleteError) throw deleteError;

  if (sections.length === 0) return;
  const { error } = await supabase
    .from('subject_sections')
    .insert(sections.map((section) => ({ subject_id: subjectId, section })));
  if (error) throw error;
}

// `subject.sections` is the list of section names; it isn't a column on subjects.
export async function createSubject({ sections = [], ...subject }) {
  const { data, error } = await supabase.from('subjects').insert(subject).select('id').single();
  if (error) throw error;
  await replaceSubjectSections(data.id, sections);
  await logActivity('subject_created', `Added subject ${subject.name}`);
  return data;
}

export async function updateSubject(id, { sections = [], ...subject }) {
  const { error } = await supabase.from('subjects').update(subject).eq('id', id);
  if (error) throw error;
  await replaceSubjectSections(id, sections);
  await logActivity('subject_updated', `Updated subject ${subject.name}`);
}

export async function deleteSubject(id, name) {
  const { error } = await supabase.from('subjects').delete().eq('id', id);
  if (error) throw error;
  await logActivity('subject_deleted', `Removed subject ${name}`);
}
